import { useState, useEffect } from "react";
import apiClient from "../../../shared/API/apiClient";

const EMPTY_FORM = {
  name: "",
  code: "",
  unit_id: "",
  min: "",
  max: "",
};

const ParameterModal = ({ onClose, onSaved, editData }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [units, setUnits] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");

  // Prefill edit data
useEffect(() => {
  if (editData && units.length > 0) {
    // match unit symbol with units list
    const matchedUnit = units.find(
      (u) => u.symbol.toLowerCase() === editData.unit?.toLowerCase()
    );

    setForm({
      name: editData.name || "",
      code: editData.code || "",
      unit_id: matchedUnit ? matchedUnit.id : "",
      min: editData.min || "",
      max: editData.max || "",
    });
  }
}, [editData, units]);

  // DROPDOWN Fetch Units API
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await apiClient.get("/api/parameters/units");

        if (res.data.code === 200) {
          setUnits(res.data.units || []);
        }
      } catch (err) {
        console.error("Units API error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, []);

  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => ({ ...p, [field]: "" }));
    setApiError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Parameter name is required";
    if (!form.unit_id) e.unit_id = "Unit is required";
    return e;
  };

  // Submit Handler (ADD + UPDATE)
  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) return setErrors(e);

    setSaving(true);
    setApiError("");

    try {
      const payload = {
        name: form.name,
        code: form.code,
        unit_id: Number(form.unit_id),
        min_limit: Number(form.min) || 0,
        max_limit: Number(form.max) || 0,
      };

      let response;

      if (editData) {
        // UPDATE API
        response = await apiClient.put(
          `/api/parameters/update/${editData.id}`,
          payload
        );
      } else {
        // ADD API
        response = await apiClient.post("/api/parameters/add", payload);
      }

      if (response.data.code === 200) {
        onSaved(
          {
            ...form,
            id: editData?.id || Date.now(),
            unit: units.find((u) => u.id == form.unit_id)?.symbol || "",
            status: "Active",
          },
          !!editData
        );
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Something went wrong";
      setApiError(msg);
    } finally {
      setSaving(false);
    }
  };



  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        
        {/* HEADER */}
        <div className="modal-header">
          <div className="modal-title">
            {editData
              ? "Edit Environmental Parameter"
              : "Add Environmental Parameter"}
          </div>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            ✕
          </button>
        </div>

        <div className="modal-subtitle">
          Define a custom parameter to track in your environmental reports.
        </div>

        <div className="modal-divider"></div>

        <br></br>

        <div className="modal-body">
          {loading ? (
            <div className="modal-loading">Loading options...</div>
          ) : (
            <>
              {/* Name */}
              <div className="org-form-row">
                <div className="org-form-field full">
                  <label className="org-field-label">
                    Parameter Name <span className="req">*</span>
                  </label>
                  <input
                    className={`org-field-input ${
                      errors.name ? "input-error" : ""
                    }`}
                    value={form.name}
                    onChange={handleChange("name")}
                  />
                  {errors.name && (
                    <span className="field-error">{errors.name}</span>
                  )}
                </div>
              </div>

              {/* Code + Unit */}
              <div className="org-form-row two">
                <div className="org-form-field">
                  <label className="org-field-label">Code</label>
                  <input
                    className="org-field-input"
                    value={form.code}
                    onChange={handleChange("code")}
                  />
                </div>

                <div className="org-form-field">
                  <label className="org-field-label">
                    Unit <span className="req">*</span>
                  </label>
                  <select
                    className={`org-field-input ${
                      errors.unit_id ? "input-error" : ""
                    }`}
                    value={form.unit_id}
                    onChange={handleChange("unit_id")}
                  >
                    <option value="">— Select Unit —</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.symbol})
                      </option>
                    ))}
                  </select>
                  {errors.unit_id && (
                    <span className="field-error">{errors.unit_id}</span>
                  )}
                </div>
              </div>

              {/* Min Max */}
              <div className="org-form-row two">
                <div className="org-form-field">
                  <label className="org-field-label">Min Limit</label>
                  <input
                    type="number"
                    className="org-field-input"
                    value={form.min}
                    onChange={handleChange("min")}
                  />
                </div>

                <div className="org-form-field">
                  <label className="org-field-label">Max Limit</label>
                  <input
                    type="number"
                    className="org-field-input"
                    value={form.max}
                    onChange={handleChange("max")}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          {apiError && <span className="org-save-error">✕ {apiError}</span>}

          <button
            className="org-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="org-btn-save"
            onClick={handleSubmit}
            disabled={loading || saving}
          >
            {saving ? "Saving..." : editData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParameterModal;